import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    this.#fetchPage('next');
    /*this.#fetchPage('previous');*/
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

#observeViewMore() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    const wrap = document.createElement('div');
    wrap.className = 'load-more-btn-wrap';

    const btn = document.createElement('button');
    btn.className = 'load-more-btn';
    btn.innerHTML = `
      <span class="load-more-btn__spinner"></span>
      <span class="load-more-btn__label">もっと見る</span>
    `;

    const backBtn = document.createElement('button');
    backBtn.className = 'load-more-back-btn';
    btn.innerHTML = `
      <span class="load-more-btn__mountain" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 460 460" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="m420.95,84.708c9.607-9.518 19.821-23.911 25.041-38.351 3.908-10.814 2.001-22.813-5.1-32.097l-8.726-11.41c-1.375-1.796-3.508-2.85-5.77-2.85h-19.51c-11.147,0-20.21,9.096-20.156,20.255 0.015,3.074-2.577,5.531-5.651,5.531h-3.246c-11.132,0-20.156,9.024-20.156,20.156v3.267c0,3.109-2.521,5.63-5.63,5.63h-3.267c-11.132,0-20.156,9.024-20.156,20.156v15.195c-6.773-1.318-14.675-2.057-23.891-2.057-8.649,0-16.321,0.631-23.108,1.889v-15.027c0-11.132-9.024-20.156-20.156-20.156h-3.267c-3.109,0-5.63-2.521-5.63-5.63v-3.267c0-11.132-9.024-20.156-20.156-20.156h-3.246c-3.075,0-5.666-2.457-5.651-5.531 0.055-11.159-9.007-20.255-20.154-20.255h-19.51c-2.262,0-4.394,1.054-5.768,2.85l-8.727,11.41c-7.101,9.284-9.008,21.283-5.1,32.097 5.219,14.44 14.575,30.143 25.041,38.351 5.178,4.061 22.895,12.986 22.895,12.986-2.16,1.138-3.75,3.315-3.885,6.141-0.086,1.797 0.583,3.549 1.707,4.954l16.663,20.829c4.085,5.107 9.795,8.452 16.107,9.548 0.196,5.796 0.579,11.231 1.136,16.323-13.011-2.059-29.93-0.826-48.945,10.108-7.553,4.343-14.083,8.403-20.397,12.33-21.196,13.179-41.215,25.627-95.787,45.896-5.794,2.152-10.908,5.274-15.338,9.341-10.003-1.744-36.652-2.518-50.942,34.024-1.198,3.066-0.185,6.554 2.468,8.502 6.052,4.439 16.3,9.513 27.797,10.903-1.33,18.392 0.2,40.61 4.59,66.268 8.94,52.257 26.393,100.346 27.132,102.367 1.047,2.865 3.772,4.771 6.822,4.771h39.802c3.743,0 6.873-2.844 7.23-6.57l10.998-117.26c4.998-6.498 8.884-14.167 10.129-22.978 0,0 57.702,6.011 76.322,11.005 11.558,3.1 18.522,15.351 18.522,27.362v101.177c0,4.011 3.252,7.263 7.263,7.263h70.929c3.731,0 6.855-2.827 7.227-6.539l16.222-161.929c1.019,0.748 2.225,1.237 3.52,1.374 2.429,0.26 4.825-0.72 6.377-2.605 29.283-35.556 13.827-93.467 7.04-113.839 2.962-13.124 3.694-26.685 3.873-37.187 6.602-0.958 12.599-4.353 16.842-9.657l16.664-20.83c1.125-1.407 1.795-3.162 1.707-4.961-0.138-2.832-1.739-5.014-3.91-6.147 5.68434e-14,0.003 18.201-8.343 22.87-12.97zm-68.64,45.396c0,23.514-1.718,46.069-10.076,61.708-4.316-17.468-16.897-30.685-32.267-33.075v-55.99c42.334,1.419 42.343,20.137 42.343,27.357zm-95.152,0c0-6.569 0.002-26.127 43.125-27.381v55.964c-15.119,2.192-27.589,14.855-32.243,31.772-4.54-6.126-10.882-37.521-10.882-60.355zm47.802,35.98c16.015,0 29.24,15.237 30.947,34.753-7.063,7.671-17.064,12.216-31.174,12.216-12.902,0-23.023-4.403-30.603-13.373 2.119-18.958 15.133-33.596 30.83-33.596zm-47.822-66.197c-3.66-1.83-12.166-3.045-16.258-3.045 0,0-32.208-16.225-42.04-23.088-8.627-6.021-16.536-20.206-20.919-32.334-2.22-6.142-1.107-12.996 2.976-18.335 0,0 4.131-7.365 6.546-8.559 3.564-1.761 15.9,0 15.9,0 3.075,0 5.666,2.457 5.651,5.531-0.054,11.159 9.009,20.255 20.156,20.255h3.267c3.109,0 5.63,2.521 5.63,5.63v3.267c0,11.132 9.024,20.156 20.156,20.156h3.267c3.109,0 5.63,2.521 5.63,5.63v19.392c-3.306,1.396-7.281,3.614-9.962,5.5zm-19.115,20.657l-7.341-9.176h14.181c0.72,0 1.438,0.081 2.147,0.211-2.036,3.813-3.372,8.042-3.982,12.632-1.935-0.752-3.666-1.993-5.005-3.667zm-185.167,126.472c-3.798,7.047-6.576,15.468-8.325,25.231-6.455-0.787-12.211-3.244-16.053-5.288 7.4-14.867 17.142-19.114 24.378-19.943zm61.668,198.458h-5.523v-85.019c3.571-1.845 8.9-4.882 14.54-9.069l-9.017,94.088zm114.91-135.347c-21.008-5.364-85.829-12.459-85.829-12.459-3.424-0.489-6.89,1.483-8.054,5.197-0.249,0.795-0.281,1.642-0.23,2.474 1.667,27.094-36.073,43.72-36.444,43.88-2.671,1.144-4.402,3.77-4.402,6.676v89.579h-8.003c-4.617-13.45-17.657-53.39-24.786-95.061-7.686-44.927-10.08-100.995 22.165-112.971 55.943-20.779 76.566-33.602 98.399-47.178 6.211-3.862 12.633-7.855 19.968-12.073 16.728-9.619 31.274-10.034 41.83-7.908-16.787,33.854-16.918,62.736-13.682,81.756 3.956,23.25 13.581,37.223 13.988,37.806 1.876,2.684 5.377,3.858 8.639,2.593 1.698-0.658 3.038-2.007 3.873-3.625l3.147-6.099c10.337,23.961 35.081,39.76 36.407,40.593 1.173,0.737 2.515,1.113 3.863,1.113 0.913,0 1.828-0.172 2.697-0.52 12.388-4.955 21.151-13.144 27.346-21.966l-14.379,143.538h-22.704l2.835-93.89c0.122-4.009-3.031-7.358-7.04-7.479-4.053-0.136-7.359,3.032-7.48,7.04l-2.849,94.328h-19.857v-93.911c0-18.296-11.691-36.907-29.418-41.433zm121.571-38.831l-6.673-12.931c-1.236-2.395-3.629-4.083-6.323-4.154-3.978-0.106-7.162,2.882-7.482,6.647-0.031,0.361-3.265,34.816-29.482,47.999-8.428-6.141-31.064-24.714-30.984-46.977 0.01-2.813-1.368-5.538-3.865-6.832-3.697-1.915-8.041-0.404-9.849,3.096l-5.995,11.617c-2.162-5.371-4.411-12.433-5.786-20.885-3.191-19.612-0.808-39.641 7.027-59.794 11.938,30.613 33.804,38.498 53.14,38.498 22.297,0 39.226-9.555 49.543-27.772 4.644,20.503 7.845,49.526-3.271,71.488zm21.271-150.752c-1.513,1.891-3.524,3.233-5.769,3.934-0.517-4.298-1.69-8.649-3.833-12.775 0.905-0.215 1.832-0.335 2.762-0.335h14.181l-7.341,9.176zm-2.906-23.702c-4.092,0-12.917,1.43-16.577,3.26-2.711-2.064-5.899-4.095-9.642-5.715v-19.392c0-3.109 2.521-5.63 5.63-5.63h3.267c11.132,0 20.156-9.024 20.156-20.156v-3.267c0-3.109 2.521-5.63 5.63-5.63h3.267c11.147,0 20.21-9.096 20.156-20.255-0.015-3.074 2.577-5.531 5.651-5.531 0,0 12.337-1.761 15.9,0 2.415,1.193 6.546,8.559 6.546,8.559 4.084,5.339 5.196,12.192 2.976,18.334-4.383,12.128-12.292,26.313-20.919,32.334-9.834,6.864-42.041,23.089-42.041,23.089z"/>
            <path d="m277.961,147.684c1.278,0 2.527-0.514 3.428-1.424 0.9-0.9 1.423-2.15 1.423-3.418 0-1.279-0.523-2.518-1.423-3.428-0.901-0.901-2.151-1.414-3.428-1.414-1.269,0-2.518,0.513-3.419,1.414-0.9,0.9-1.423,2.15-1.423,3.428 0,1.278 0.523,2.527 1.423,3.418 0.9,0.911 2.149,1.424 3.419,1.424z"/>
            <path d="m327.485,146.27c0.9,0.9 2.15,1.414 3.428,1.414 1.268,0 2.518-0.514 3.419-1.414 0.91-0.91 1.423-2.15 1.423-3.428 0-1.279-0.514-2.528-1.423-3.428-0.901-0.901-2.151-1.414-3.419-1.414-1.279,0-2.528,0.513-3.428,1.414-0.9,0.9-1.414,2.15-1.414,3.428 0,1.278 0.514,2.518 1.414,3.428z"/>
            <path d="m304.733,204.821c10.648,0 20.649-9.535 20.649-15.284 0-6.807-11.124-9.222-20.649-9.222-9.525,0-20.649,2.415-20.649,9.222 0,5.749 10.001,15.284 20.649,15.284z"/>
          </g>
        </svg>
      </span>
      <span class="load-more-btn__label">もっと見る</span>
    `;
    backBtn.style.display = 'none';

    wrap.appendChild(btn);
    wrap.appendChild(backBtn);
    viewMoreNext.parentNode.insertBefore(wrap, viewMoreNext.nextSibling);

    backBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    const updateButton = () => {
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) {
        btn.style.display = 'none';
        backBtn.style.display = 'inline-flex';
      }
    };

    btn.addEventListener('click', async () => {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.querySelector('.load-more-btn__label').textContent = '読み込み中...';

      await this.#renderNextPage();

      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.querySelector('.load-more-btn__label').textContent = 'もっと見る';

      updateButton();
    });

    updateButton();
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    await this.#fetchSpecificPage(page.page, page.url);

    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // After a filter update, pagination typically resets to page 1
    // Fetch page 2 as the "next" page since page 1 is already visible
    this.#fetchSpecificPage(2);
  };
}
